import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import Artwork from '../components/Artwork.jsx'
import Avatar from '../components/Avatar.jsx'
import { api, ApiError } from '../lib/api.js'

/**
 * Search (PLAN §7, §12).
 *
 * The two-step flow is the product decision, not an implementation detail:
 * typing searches what Sonologue already knows, instantly and for free, and
 * only an explicit "search MusicBrainz" spends a request against a donated
 * budget. That is why the remote results live in their own state and their
 * own list rather than being merged into the local ones — a person should
 * always be able to tell which of the two they are looking at.
 */

const DEBOUNCE_MS = 250
const MIN_REMOTE_LENGTH = 2

const KIND_LABEL = {
  release_group: 'Album',
  artist: 'Artist',
  recording: 'Song',
}

/**
 * What the remote slot says about a failed ask or a failed open.
 *
 * The API's sentence first, this page's own only when `fetch` itself
 * rejected — where `err.message` is the browser's *"Failed to fetch"* or
 * Safari's *"Load failed"*, which is jargon addressed to nobody.
 */
function remoteMessage(err) {
  if (!(err instanceof ApiError)) return 'That did not go through. Try again.'
  if (err.code === 'provider-unavailable') {
    return 'MusicBrainz is not answering right now. Everything already in Sonologue still works.'
  }
  return err.message
}

/** `catalog/router.py:61` — the API's `max_length` for `q`. */
const SEARCH_MAX = 200

/**
 * What the local slot says about a failed search.
 *
 * Branching on the code rather than the status, because the code is the stable
 * thing (AGENTS.md §Conventions). `validation-failed` means exactly one thing
 * here: `q` is the only validated parameter this page sends, so the refusal is
 * about length — and the problem document's own words for it, both the `detail`
 * and pydantic's `errors[]` row, are addressed to whoever wrote the schema.
 */
function localMessage(err) {
  if (!(err instanceof ApiError)) return 'Search is not answering right now.'
  if (err.code === 'validation-failed') {
    return `That search is too long. Sonologue looks up to ${SEARCH_MAX} characters; try something shorter.`
  }
  return err.message
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="5.2" stroke="currentColor" strokeWidth="1.6" />
      <line
        x1="11"
        y1="11"
        x2="14.6"
        y2="14.6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}

function Group({ title, children }) {
  return (
    <section className="result-group fade-up">
      <h3>{title}</h3>
      <div className="card hairline-list">{children}</div>
    </section>
  )
}

export default function Search() {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const initial = params.get('q') || ''

  const [query, setQuery] = useState(initial)
  const [results, setResults] = useState(null)
  const [error, setError] = useState(null)
  const [searching, setSearching] = useState(false)

  const [remote, setRemote] = useState(null)
  const [remoteError, setRemoteError] = useState(null)
  const [remoteBusy, setRemoteBusy] = useState(false)
  const [opening, setOpening] = useState(null)

  const latest = useRef(0)

  useEffect(() => {
    document.title = 'Search — Sonologue'
  }, [])

  // Local search, debounced. Every keystroke abandons the previous request
  // rather than racing it: results arriving out of order is the classic
  // search-box bug and the fix is cheap.
  useEffect(() => {
    const trimmed = query.trim()
    setRemote(null)
    setRemoteError(null)

    if (!trimmed) {
      setResults(null)
      setError(null)
      return undefined
    }

    const ticket = (latest.current += 1)
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      setSearching(true)
      try {
        const body = await api.search(trimmed, { signal: controller.signal })
        if (ticket === latest.current) {
          setResults(body)
          setError(null)
        }
      } catch (err) {
        if (err.name === 'AbortError') return
        if (ticket === latest.current) setError(err)
      } finally {
        if (ticket === latest.current) setSearching(false)
      }
    }, DEBOUNCE_MS)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [query])

  // Keep the URL in step, so a search is a link somebody can send.
  useEffect(() => {
    const trimmed = query.trim()
    const current = params.get('q') || ''
    if (trimmed === current) return
    setParams(trimmed ? { q: trimmed } : {}, { replace: true })
  }, [query, params, setParams])

  const askMusicBrainz = useCallback(async () => {
    setRemoteBusy(true)
    setRemoteError(null)
    try {
      const body = await api.searchRemote(query.trim(), { type: 'albums,artists,songs' })
      setRemote(body.results)
    } catch (err) {
      setRemoteError(err)
    } finally {
      setRemoteBusy(false)
    }
  }, [query])

  async function open(hit) {
    setOpening(hit.mbid)
    setRemoteError(null)
    try {
      const entity = await api.materialize({ entity_type: hit.entity_type, mbid: hit.mbid })
      navigate(entity.path)
    } catch (err) {
      setRemoteError(err)
      setOpening(null)
    }
  }

  const empty = results && !results.has_results
  const canAskRemote = query.trim().length >= MIN_REMOTE_LENGTH

  return (
    <>
      <h1 className="large-title">Search</h1>
      <p className="page-intro">
        Artists, albums and songs already in Sonologue. Anything else is one click away in
        MusicBrainz.
      </p>

      <div className="search-field">
        <SearchIcon />
        <input
          className="search-input"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search artists, albums, songs"
          aria-label="Search"
          autoFocus
        />
      </div>

      {error ? (
        <div className="form-alert" role="alert">
          {localMessage(error)}
        </div>
      ) : null}

      {!query.trim() ? (
        <div className="card">
          <div className="empty-state">
            <h2>Start typing</h2>
            <p>
              Sonologue only knows the music somebody has looked up. If a search comes back empty,
              fetch it from MusicBrainz and it is here for everyone.
            </p>
          </div>
        </div>
      ) : null}

      {results?.artists?.length ? (
        <Group title="Artists">
          {results.artists.map((artist) => (
            <Link key={artist.public_id} className="row" to={`/artist/${artist.public_id}`}>
              <Avatar name={artist.name} size={44} />
              <div className="row-main">
                <div className="row-title">{artist.name}</div>
                <div className="row-sub">{artist.disambiguation || 'Artist'}</div>
              </div>
              <span className="faint" aria-hidden="true">
                ›
              </span>
            </Link>
          ))}
        </Group>
      ) : null}

      {results?.albums?.length ? (
        <Group title="Albums">
          {results.albums.map((album) => (
            <Link key={album.public_id} className="row" to={`/album/${album.public_id}`}>
              <Artwork
                art={album.artwork}
                title={album.title}
                artist={album.credit_name}
                size={44}
              />
              <div className="row-main">
                <div className="row-title">{album.title}</div>
                <div className="row-sub">
                  {[album.credit_name, album.year, album.primary_type].filter(Boolean).join(' · ')}
                </div>
              </div>
              <span className="faint" aria-hidden="true">
                ›
              </span>
            </Link>
          ))}
        </Group>
      ) : null}

      {results?.songs?.length ? (
        <Group title="Songs">
          {results.songs.map((song) => (
            <Link key={song.public_id} className="row" to={`/song/${song.public_id}`}>
              <div className="row-main">
                <div className="row-title">{song.title}</div>
                <div className="row-sub">{song.credit_name}</div>
              </div>
              <span className="faint" aria-hidden="true">
                ›
              </span>
            </Link>
          ))}
        </Group>
      ) : null}

      {empty && !remote ? (
        <div className="card">
          <div className="empty-state">
            <h2>Nothing here yet</h2>
            <p>
              Nobody has looked up “{query.trim()}” before. MusicBrainz almost certainly has it —
              fetching adds it to Sonologue for everyone.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={askMusicBrainz}
              disabled={!canAskRemote || remoteBusy}
            >
              {remoteBusy ? 'Asking MusicBrainz…' : 'Search MusicBrainz'}
            </button>
          </div>
        </div>
      ) : null}

      {results?.has_results && !remote ? (
        <p className="footnote">
          Not the one you meant?{' '}
          <button
            type="button"
            className="link-button"
            onClick={askMusicBrainz}
            disabled={!canAskRemote || remoteBusy}
          >
            {remoteBusy ? 'Asking MusicBrainz…' : 'Search MusicBrainz'}
          </button>
        </p>
      ) : null}

      {remoteError ? (
        <div className="form-alert" role="alert">
          {remoteMessage(remoteError)}
        </div>
      ) : null}

      {remote ? (
        <section className="result-group fade-up">
          <h3>From MusicBrainz</h3>
          {remote.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <h2>MusicBrainz has nothing either</h2>
                <p>Check the spelling, or try the artist’s name on its own.</p>
              </div>
            </div>
          ) : (
            <div className="card hairline-list">
              {remote.map((hit) => (
                <button
                  key={`${hit.entity_type}:${hit.mbid}`}
                  type="button"
                  className="row hoverable remote-row"
                  onClick={() => open(hit)}
                  disabled={opening !== null}
                >
                  <span className="phase-tag neutral">{KIND_LABEL[hit.entity_type]}</span>
                  <span className="row-main">
                    <span className="row-title">{hit.title}</span>
                    <span className="row-sub">
                      {[hit.subtitle, hit.disambiguation].filter(Boolean).join(' — ')}
                    </span>
                  </span>
                  <span className="footnote">{opening === hit.mbid ? 'Adding…' : 'Add'}</span>
                </button>
              ))}
            </div>
          )}
          <p className="caption mt-2">
            Metadata from MusicBrainz, which is CC0. Adding one stores a local copy so the next
            search is instant.
          </p>
        </section>
      ) : null}

      {searching && !results ? <p className="subtle">Searching…</p> : null}
    </>
  )
}
